/**
 * Reconcile an optimistic "Response stopped" note against stored truth.
 *
 * Pressing Stop aborts the fetch, which is the ONLY signal the client has.
 * It is not the same thing as the turn having been cut short. The server
 * commits the assistant row as soon as the model finishes, and only THEN
 * runs summary + title generation — two real LLM calls — before yielding
 * the final `complete` event (see
 * ``ConversationService.send_message_stream``: the commit at the
 * "POST-STREAM WRITE COMMIT" block, ``_generate_conversation_summary`` /
 * ``_generate_conversation_title`` below it, and the `complete` yield last).
 * A Stop pressed anywhere in that multi-second gap aborts a stream whose
 * answer had ALREADY completed and been persisted, and the client — which
 * never received `complete` — paints "Response stopped" on a turn that
 * finished. The database says `interrupted=false`, a reload shows the full
 * answer, so the note is a purely live-view lie.
 *
 * The client cannot know which case it is without asking, so it asks: one
 * short re-read of the conversation's persisted messages a moment after the
 * abort settles. This module holds the pure decision that re-read feeds.
 *
 * The rule is deliberately one-directional — it only ever CLEARS the note,
 * and only on positive evidence:
 *
 *   completed   — the last persisted message is an assistant row that is NOT
 *                 flagged interrupted. The turn finished; drop the note and
 *                 show the persisted text.
 *   interrupted — the last persisted message is an assistant row flagged
 *                 interrupted (written by ``_persist_interrupted_turn``).
 *                 A genuine mid-stream stop; keep the note and the frozen
 *                 text, and show what the stored answer records beside it
 *                 — its Sources and the release that served it.
 *   unknown     — no assistant row for this turn yet (the tail is still the
 *                 user's message), or the fetch failed. No evidence, so the
 *                 optimistic note stands.
 *
 * Why "last message is an assistant row" identifies THIS turn and not a
 * previous one: the user's message for the turn is committed and yielded
 * before a single token streams (``send_message_stream`` commits, then
 * yields `user_message`), and the continuation flow persists its synthetic
 * user message before the stream opens. So until this turn's assistant row
 * lands, the tail IS this turn's user message — never a stale assistant
 * row. Both "too early" outcomes therefore land on `unknown`, which keeps
 * today's behaviour. There is no timing at which this can produce a false
 * `completed`. Once the person has sent their next message the tail is no
 * longer this turn's, so a panel that knows the stopped turn's user message
 * (or, for a Continue, the answer) names it (`StoppedTurn`).
 */

/** Structural shape common to both transcripts. `AoAMessage` (sender_type /
 *  message_content / message_metadata) and the regular chat's `Message`
 *  (role / content / interrupted) are both assignable to it, so one helper
 *  serves the AoA panel and ConversationPanel without either owning it. */
export interface PersistedMessageLike {
  message_id?: string;
  sender_type?: string;
  role?: string;
  message_content?: string;
  content?: string;
  message_metadata?: Record<string, unknown> | null;
  /** Flattened by ``conversationService.getMessages`` from
   *  ``message_metadata.interrupted`` — the regular chat's `Message` never
   *  carries raw metadata. */
  interrupted?: boolean;
}

export type StopVerdict<M extends PersistedMessageLike = PersistedMessageLike> =
  /** The turn finished server-side; `content` is the persisted answer. */
  | { kind: 'completed'; content: string; message: M }
  /** The turn really was cut short — keep the note. `message` is the stored
   *  stopped answer: what it searched (`web_sources`) and which release
   *  served it, for the panel to show under the frozen text. */
  | { kind: 'interrupted'; message: M }
  /** Nothing persisted for this turn yet — keep the optimistic note. */
  | { kind: 'unknown' };

/** Which stored row is the stopped turn's answer. */
export interface StoppedTurn {
  /** A stopped Continue: the answer it was growing. */
  messageId?: string | null;
  /** The stopped turn's own user message: its answer is the row after it.
   *  Without it (Stop before the server confirmed the message) the
   *  transcript's last row is read, as before. */
  afterMessageId?: string | null;
}

/**
 * How long to wait after the abort before the single re-read.
 *
 * Sized against the two things that must have settled: the socket teardown
 * that trips ``pump_stream``'s `abandoned` flag, and — on a genuine
 * mid-stream stop — ``_persist_interrupted_turn`` writing its row. It does
 * NOT need to be generous: in the post-completion case the row was already
 * committed BEFORE the click, so it is readable at once, and every other
 * case degrades to `unknown`, which changes nothing. That is the first read:
 * while it finds no row for the turn, a few more follow
 * (STOP_RECONCILE_RETRY_MS) — never past one.
 */
export const STOP_RECONCILE_DELAY_MS = 600;

function roleOf(m: PersistedMessageLike): string {
  return m.sender_type ?? m.role ?? '';
}

function contentOf(m: PersistedMessageLike): string {
  return m.message_content ?? m.content ?? '';
}

function isInterrupted(m: PersistedMessageLike): boolean {
  if (m.interrupted === true) return true;
  const meta = m.message_metadata;
  return !!meta && meta.interrupted === true;
}

function storedAnswer<M extends PersistedMessageLike>(
  messages: readonly M[], turn: StoppedTurn,
): M | undefined {
  if (turn.messageId) return messages.find((m) => m.message_id === turn.messageId);
  if (turn.afterMessageId) {
    const at = messages.findIndex((m) => m.message_id === turn.afterMessageId);
    return at < 0 ? undefined : messages[at + 1];
  }
  return messages[messages.length - 1];
}

/**
 * Decide what the live view should say about the turn that was just
 * stopped, given the conversation's persisted messages (oldest first).
 * With `turn`, the stopped turn's own row is read even after the person has
 * already sent their next message.
 */
export function reconcileStoppedTurn<M extends PersistedMessageLike>(
  messages: readonly M[] | null | undefined,
  turn: StoppedTurn = {},
): StopVerdict<M> {
  if (!messages || messages.length === 0) return { kind: 'unknown' };
  const answer = storedAnswer(messages, turn);
  if (!answer || roleOf(answer) !== 'assistant') return { kind: 'unknown' };
  if (isInterrupted(answer)) return { kind: 'interrupted', message: answer };
  const content = contentOf(answer);
  // A committed-but-empty assistant row is a propose-only turn (the AoA
  // persona is allowed to answer with a card and no prose). There is no
  // text to promote and no note to justify, so leave the view alone.
  if (!content.trim()) return { kind: 'unknown' };
  return { kind: 'completed', content, message: answer };
}

/**
 * The waits between further re-reads while the stopped turn's row has not
 * landed. The stopped row is written only after the model call in flight has
 * ended and written its cost (TurnRecord.settle, ≤ 1.5 s), so a single read
 * at STOP_RECONCILE_DELAY_MS often came too early — and the answer showed no
 * Sources until a reload (staging 2026-10-20). Bounded: ~8 s in all.
 */
export const STOP_RECONCILE_RETRY_MS: readonly number[] = [500, 1000, 2000, 4000];

/**
 * Read the stored transcript until `judge` gives a verdict, at most
 * 1 + `retryMs.length` times. A failed read is no evidence (`unknown`), and a
 * cancelled settle (the panel moved to another conversation) answers
 * `unknown` without reading again.
 */
export async function settleStoppedTurn<M extends PersistedMessageLike>(
  read: () => Promise<readonly M[]>,
  judge: (messages: readonly M[]) => StopVerdict<M>,
  cancelled: () => boolean,
  retryMs: readonly number[] = STOP_RECONCILE_RETRY_MS,
): Promise<StopVerdict<M>> {
  for (let attempt = 0; ; attempt += 1) {
    if (cancelled()) return { kind: 'unknown' };
    let verdict: StopVerdict<M> = { kind: 'unknown' };
    try {
      verdict = judge(await read());
    } catch {
      /* no evidence */
    }
    if (cancelled()) return { kind: 'unknown' };
    if (verdict.kind !== 'unknown' || attempt >= retryMs.length) return verdict;
    await new Promise<void>((resolve) => { setTimeout(resolve, retryMs[attempt]); });
  }
}
